import { useRef } from 'react';
import { Alert, Button, CircularProgress, DialogContent } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import AppDialog from '../../../components/AppDialog';
import { useModalHistory } from '../../../utils/useModalHistory';
import type { ImportResult } from '../../planning/hooks/useImportMachines';
import { DASHBOARD_LABELS as LABELS, IMPORT_LABELS } from '../../../utils/labels';

interface ImportMachinesDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string | null;
  downloading: boolean;
  importing: boolean;
  importResult: ImportResult | null;
  downloadTemplate: () => void;
  importMachines: (file: File) => void;
  resetImport: () => void;
}

export default function ImportMachinesDialog({
  open,
  onClose,
  projectId,
  downloading,
  importing,
  importResult,
  downloadTemplate,
  importMachines,
  resetImport,
}: ImportMachinesDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  useModalHistory(onClose, open);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) importMachines(file);
          e.target.value = '';
        }}
      />

      <AppDialog
        open={open}
        onClose={onClose}
        title={LABELS.importDialogTitle}
        fullScreenOnMobile={false}
        maxWidth="xs"
        fullWidth
        testId="import-machines-dialog"
      >
        <DialogContent className="flex flex-col gap-4 pt-2">
          {importResult && importResult.errors.length > 0 && (
            <Alert severity="warning" onClose={resetImport}>
              <p className="mb-1">
                {IMPORT_LABELS.importPartial(importResult.added, importResult.skipped)}
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                {importResult.errors.map((e, i) => (
                  <li key={i} className="text-sm">
                    {e}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {importResult && importResult.warnings.length > 0 && (
            <Alert severity="info" onClose={resetImport}>
              <ul className="list-disc pl-4 space-y-0.5">
                {importResult.warnings.map((w, i) => (
                  <li key={i} className="text-sm">
                    {w}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <div className="border border-border rounded-lg p-4 flex flex-col gap-3">
            <div>
              <p className="font-semibold text-sm mb-1">{LABELS.importTemplateAction}</p>
              <p className="text-sm text-text-secondary">{LABELS.importTemplateDesc}</p>
            </div>
            <Button
              variant="outlined"
              size="small"
              startIcon={downloading ? <CircularProgress size="1rem" /> : <DownloadIcon />}
              disabled={!projectId || downloading}
              onClick={downloadTemplate}
              data-testid="download-template-button"
            >
              {LABELS.importTemplateAction}
            </Button>
          </div>

          <div className="border border-border rounded-lg p-4 flex flex-col gap-3">
            <div>
              <p className="font-semibold text-sm mb-1">{LABELS.importUploadAction}</p>
              <p className="text-sm text-text-secondary">{LABELS.importUploadDesc}</p>
            </div>
            <Button
              variant="outlined"
              size="small"
              startIcon={importing ? <CircularProgress size="1rem" /> : <UploadIcon />}
              disabled={!projectId || importing}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-button"
            >
              {LABELS.importUploadAction}
            </Button>
          </div>
        </DialogContent>
      </AppDialog>
    </>
  );
}
